/**
 * TransactionDetailModal - Admin interface for viewing transaction details
 * Supports: Quotes, Invoices, Receipts, Refunds, and other transaction types
 * Key Feature: "Convert to Invoice" button for Quotes
 */
import React, { useState } from 'react';
import { X, FileText, Receipt, RefreshCcw, Loader, CheckCircle2, AlertCircle, DollarSign, AlertTriangle } from 'lucide-react';
import { syncManager } from '../../services/sync';

const TransactionDetailModal = ({ transaction, onClose, onSuccess }) => {
    const [converting, setConverting] = useState(false);
    const [refunding, setRefunding] = useState(false);
    const [voiding, setVoiding] = useState(false);
    const [error, setError] = useState(null);

    // Determine transaction type icon and color
    const getTypeConfig = (type) => {
        switch (type?.toLowerCase()) {
            case 'quote':
                return { icon: <FileText size={20} />, color: 'bg-blue-100 text-blue-600', border: 'border-blue-200' };
            case 'invoice':
                return { icon: <Receipt size={20} />, color: 'bg-emerald-100 text-emerald-600', border: 'border-emerald-200' };
            case 'receipt':
                return { icon: <CheckCircle2 size={20} />, color: 'bg-indigo-100 text-indigo-600', border: 'border-indigo-200' };
            default:
                return { icon: <FileText size={20} />, color: 'bg-slate-100 text-slate-600', border: 'border-slate-200' };
        }
    };

    const typeConfig = getTypeConfig(transaction?.type);
    const isQuote = transaction?.type?.toLowerCase() === 'quote';
    const isInvoice = transaction?.type?.toLowerCase() === 'invoice';
    const isPaidInvoice = isInvoice && transaction?.status?.toLowerCase() === 'paid';

    const formatCurrencyLocal = (val) => {
        return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(val || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) {return 'N/A';}
        try {
            return new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    const handleConvertToInvoice = async () => {
        if (!confirm(`Convert Quote #${transaction.invoiceId || transaction.id} to Invoice? This action cannot be undone.`)) {
            return;
        }

        setConverting(true);
        setError(null);

        try {
            const txId = transaction.invoiceId || transaction.id;
            await syncManager.triggerWorkflow('hq-quote-to-invoice', { invoiceId: txId });

            alert(`Success: Quote #${txId} has been converted to an Invoice.`);

            if (onSuccess) {onSuccess();}
            onClose();
        } catch (err) {
            console.error('Quote-to-Invoice conversion failed:', err);
            setError(`Conversion failed: ${err.message}`);
        } finally {
            setConverting(false);
        }
    };

    const handleRefund = async () => {
        const txId = transaction.invoiceId || transaction.id;
        const refundAmount = prompt(`Enter refund amount for Invoice #${txId}:`, transaction.amount);
        if (!refundAmount || isNaN(refundAmount) || parseFloat(refundAmount) <= 0) {
            return;
        }

        if (!confirm(`Process refund of ${formatCurrencyLocal(refundAmount)} for Invoice #${txId}?`)) {
            return;
        }

        setRefunding(true);
        setError(null);

        try {
            const reissueInvoice = confirm('Would you like to re-issue the invoice with a corrected amount?');
            await syncManager.triggerWorkflow('process-refund', {
                invoiceId: txId,
                refundAmount: parseFloat(refundAmount),
                reissueInvoice
            });
            alert(`Success: Refund of ${formatCurrencyLocal(refundAmount)} processed for Invoice #${txId}.`);
            if (onSuccess) {onSuccess();}
            onClose();
        } catch (err) {
            console.error('Refund failed:', err);
            setError(`Refund failed: ${err.message}`);
        } finally {
            setRefunding(false);
        }
    };

    const handleVoid = async () => {
        const txId = transaction.invoiceId || transaction.id;
        if (!confirm(`⚠️ WARNING: This will VOID Invoice #${txId} and reverse all revenue and commissions.\n\nThis action cannot be undone. Continue?`)) {
            return;
        }

        setVoiding(true);
        setError(null);

        try {
            await syncManager.triggerWorkflow('billing-void-transaction', { invoiceId: txId });
            alert(`Success: Invoice #${txId} has been voided.`);
            if (onSuccess) {onSuccess();}
            onClose();
        } catch (err) {
            console.error('Void failed:', err);
            setError(`Void failed: ${err.message}`);
        } finally {
            setVoiding(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="bg-slate-50 border-b border-slate-100 p-6 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-2xl ${typeConfig.color} shadow-sm`}>
                            {typeConfig.icon}
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">
                                {transaction?.type || 'Transaction'} Details
                            </h2>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                                {transaction?.invoiceId || transaction?.id || 'N/A'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                    <div className="space-y-6">
                        {/* Status Badge */}
                        <div className="flex items-center justify-between">
                            <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${transaction?.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' :
                                transaction?.status === 'Active' || transaction?.status === 'Sent' ? 'bg-blue-100 text-blue-700' :
                                    transaction?.status?.toLowerCase()?.includes('overdue') ? 'bg-rose-100 text-rose-700' :
                                        'bg-slate-100 text-slate-700'
                                }`}>
                                {transaction?.status || 'Unknown'}
                            </span>
                            {transaction?.createdAt && (
                                <span className="text-[10px] font-bold text-slate-400 uppercase">
                                    Created: {formatDate(transaction.createdAt)}
                                </span>
                            )}
                        </div>

                        {/* Amount Section */}
                        <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Amount</p>
                            <p className="text-3xl font-black text-slate-900 tracking-tight">
                                {formatCurrencyLocal(transaction?.amount)}
                            </p>
                            {transaction?.balance > 0 && (
                                <p className="text-[10px] font-black text-rose-600 uppercase tracking-tighter mt-2">
                                    Balance Due: {formatCurrencyLocal(transaction.balance)}
                                </p>
                            )}
                        </div>

                        {/* Client Details */}
                        <div className="space-y-3">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client Information</p>
                            <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                <p className="font-bold text-slate-800">{transaction?.clientName || 'N/A'}</p>
                                <p className="text-sm text-slate-500 lowercase">{transaction?.clientEmail || 'N/A'}</p>
                            </div>
                        </div>

                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Date</p>
                                <p className="font-bold text-slate-700">{formatDate(transaction?.date || transaction?.invoiceDate)}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                                    {isQuote ? 'Valid Until' : 'Due Date'}
                                </p>
                                <p className="font-bold text-slate-700">{formatDate(transaction?.dueDate || transaction?.validUntil)}</p>
                            </div>
                        </div>

                        {/* Description/Notes */}
                        {transaction?.description && (
                            <div className="space-y-2">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Description</p>
                                <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-4 border border-slate-100">
                                    {transaction.description}
                                </p>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-center gap-3">
                                <AlertCircle size={20} className="text-rose-500 shrink-0" />
                                <p className="text-sm font-bold text-rose-700">{error}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="bg-slate-50 border-t border-slate-100 p-6 shrink-0 space-y-4">
                    {/* Convert to Invoice Button - ONLY for Quotes */}
                    {isQuote && (
                        <button
                            onClick={handleConvertToInvoice}
                            disabled={converting}
                            className="w-full py-4 px-6 rounded-2xl bg-blue-600 text-white font-black uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 transition-all shadow-xl hover:bg-blue-700 active:scale-95 disabled:opacity-50 disabled:scale-100 disabled:shadow-none"
                        >
                            {converting ? (
                                <>
                                    <Loader className="animate-spin" size={18} />
                                    Converting...
                                </>
                            ) : (
                                <>
                                    <RefreshCcw size={18} />
                                    Convert to Invoice
                                </>
                            )}
                        </button>
                    )}

                    {/* Refund Button - ONLY for Invoices */}
                    {isInvoice && (
                        <button
                            onClick={handleRefund}
                            disabled={refunding || voiding}
                            className="w-full py-4 px-6 rounded-2xl bg-amber-500 text-white font-black uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 transition-all shadow-xl hover:bg-amber-600 active:scale-95 disabled:opacity-50 disabled:scale-100 disabled:shadow-none"
                        >
                            {refunding ? (
                                <>
                                    <Loader className="animate-spin" size={18} />
                                    Processing Refund...
                                </>
                            ) : (
                                <>
                                    <DollarSign size={18} />
                                    Issue Refund
                                </>
                            )}
                        </button>
                    )}

                    {/* Void Button - ONLY for Invoices (below Refund) */}
                    {isInvoice && (
                        <button
                            onClick={handleVoid}
                            disabled={voiding || refunding}
                            className="w-full py-4 px-6 rounded-2xl bg-rose-600 text-white font-black uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 transition-all shadow-xl hover:bg-rose-700 active:scale-95 disabled:opacity-50 disabled:scale-100 disabled:shadow-none"
                        >
                            {voiding ? (
                                <>
                                    <Loader className="animate-spin" size={18} />
                                    Voiding Invoice...
                                </>
                            ) : (
                                <>
                                    <AlertTriangle size={18} />
                                    Void Invoice
                                </>
                            )}
                        </button>
                    )}

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="w-full py-4 px-6 rounded-2xl bg-white border-2 border-slate-200 text-slate-700 font-black uppercase tracking-widest text-xs hover:bg-slate-50 transition-all"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TransactionDetailModal;
